import React, { useEffect, createElement } from 'react';
function injectSchema(id: string, schema: object) {
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schema);
  return () => {
    script?.remove();
  };
}
export function OrganizationSchema() {
  useEffect(() => {
    return injectSchema('vitality-org-schema', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Vitality Venture Fund',
      alternateName: 'Vitality Vida Partners, LLC',
      url: 'https://www.vitalityvf.com',
      logo: 'https://cdn.magicpatterns.com/uploads/rLCDkCKprcJW1NkEV2aCi1/Vitality_Logo_Circle.png',
      description: "Vitality is a venture fund investing in deep tech and hard tech solutions rooted in biomimicry, circular systems, and resilience. We fuel startups built on nature's 3.8 billion years of R&D.",
      foundingDate: '2024',
      founder: {
        '@type': 'Person',
        name: 'Natalya Wallin',
        jobTitle: 'GP & Managing Director',
        url: 'https://www.vitalityvf.com/founders',
        sameAs: ['https://www.linkedin.com/company/vitality-vf/']
      },
      sameAs: ['https://www.linkedin.com/company/vitality-vf/'],
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'natalya@vitalityvf.com',
        contactType: 'General Inquiry'
      },
      knowsAbout: ['Biomimicry', 'Venture Capital', 'Nature-Inspired Innovation', 'Regenerative Agriculture', 'Deep Tech', 'Hard Tech', 'Climate Tech', 'Circular Economy', 'Bio-based Materials'],
      areaServed: ['North America', 'United Kingdom']
    });
  }, []);
  return null;
}
export function WebSiteSchema() {
  useEffect(() => {
    return injectSchema('vitality-website-schema', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Vitality Venture Fund',
      url: 'https://www.vitalityvf.com',
      description: 'Venture fund investing in nature-inspired deep tech and hard tech solutions rooted in biomimicry.',
      publisher: {
        '@type': 'Organization',
        name: 'Vitality Venture Fund',
        url: 'https://www.vitalityvf.com'
      }
    });
  }, []);
  return null;
}
export function BreadcrumbSchema({
  items
}: {
  items: {
    name: string;
    url: string;
  }[];
}) {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url
      }))
    };
    return injectSchema('vitality-breadcrumb-schema', schema);
  }, [items]);
  return null;
}
export function InvestmentFundSchema() {
  useEffect(() => {
    return injectSchema('vitality-fund-schema', {
      '@context': 'https://schema.org',
      '@type': 'InvestmentFund',
      name: 'Vitality Venture Fund',
      url: 'https://www.vitalityvf.com',
      description: 'Pre-seed and seed stage venture fund investing in biomimicry-inspired deep tech startups across agriculture, construction, and materials.',
      investmentType: 'Venture Capital',
      areaServed: ['North America', 'United Kingdom'],
      founder: {
        '@type': 'Person',
        name: 'Natalya Wallin',
        jobTitle: 'GP & Managing Director'
      }
    });
  }, []);
  return null;
}
export function PersonSchema({
  name,
  jobTitle,
  description,
  image,
  url
}: {
  name: string;
  jobTitle: string;
  description: string;
  image?: string;
  url?: string;
}) {
  useEffect(() => {
    return injectSchema(`vitality-person-${name.replace(/\s/g, '-').toLowerCase()}`, {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name,
      jobTitle,
      description,
      image,
      url: url || 'https://www.vitalityvf.com/founders',
      worksFor: {
        '@type': 'Organization',
        name: 'Vitality Venture Fund',
        url: 'https://www.vitalityvf.com'
      }
    });
  }, [name, jobTitle, description, image, url]);
  return null;
}
export function FAQPageSchema({
  faqs
}: {
  faqs: {
    question: string;
    answer: string;
  }[];
}) {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    };
    return injectSchema('vitality-faq-schema', schema);
  }, [faqs]);
  return null;
}
export function ArticleSchema({
  title,
  description,
  datePublished,
  url
}: {
  title: string;
  description: string;
  datePublished: string;
  url: string;
}) {
  useEffect(() => {
    return injectSchema(`vitality-article-${title.replace(/\s/g, '-').toLowerCase()}`, {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description,
      datePublished,
      url,
      author: {
        '@type': 'Organization',
        name: 'Vitality Venture Fund',
        url: 'https://www.vitalityvf.com'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Vitality Venture Fund',
        url: 'https://www.vitalityvf.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://cdn.magicpatterns.com/uploads/rLCDkCKprcJW1NkEV2aCi1/Vitality_Logo_Circle.png'
        }
      }
    });
  }, [title, description, datePublished, url]);
  return null;
}